import { withModelHeader } from "./utils/modelHeaders";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export class ApiError extends Error {
  constructor(message, { status, detail } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

export const apiGet = (path, options = {}) => apiRequest(path, { ...options, method: "GET" });
export const apiPost = (path, body, options = {}) => apiRequest(path, { ...options, method: "POST", body });
export const apiPut = (path, body, options = {}) => apiRequest(path, { ...options, method: "PUT", body });
export const apiPatch = (path, body, options = {}) => apiRequest(path, { ...options, method: "PATCH", body });
export const apiDelete = (path, options = {}) => apiRequest(path, { ...options, method: "DELETE" });
export const apiWebSocketUrl = (path, token) => {
  const base = API_BASE_URL || window.location.origin;
  const url = new URL(path, base);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  if (token) url.searchParams.set("token", token);
  return url.toString();
};

export async function apiRequest(path, { token, model, method = "GET", body, headers = {}, signal } = {}) {
  const request = buildRequest({ token, model, method, body, headers, signal });

  let response = await fetch(apiUrl(path), request);
  if (response.status === 401 && token && !path.startsWith("/auth/")) {
    const refreshedToken = await refreshAccessToken();
    if (refreshedToken) {
      response = await fetch(apiUrl(path), buildRequest({ token: refreshedToken, model, method, body, headers, signal }));
    }
  }
  const data = await parseResponse(response);
  if (!response.ok) throw apiError(response, data);
  return data;
}

const buildRequest = ({ token, model, method, body, headers, signal }) => {
  const requestHeaders = authHeaders(token, model, headers);
  const request = { method, headers: requestHeaders, signal, credentials: "include" };

  if (body !== undefined) {
    if (body instanceof FormData) {
      request.body = body;
    } else {
      requestHeaders["Content-Type"] = requestHeaders["Content-Type"] || "application/json";
      request.body = typeof body === "string" ? body : JSON.stringify(body);
    }
  }
  return request;
};

let refreshPromise = null;
export function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = fetch(apiUrl("/auth/refresh"), { method: "POST", credentials: "include" })
    .then(async (response) => {
      const data = await parseResponse(response);
      if (!response.ok || !data?.access_token) throw apiError(response, data);
      localStorage.setItem("access_token", data.access_token);
      window.dispatchEvent(new CustomEvent("taskit:access-token", { detail: data.access_token }));
      return data.access_token;
    })
    .catch(() => {
      localStorage.removeItem("access_token");
      window.dispatchEvent(new CustomEvent("taskit:access-token", { detail: null }));
      return null;
    })
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
}

export async function downloadFile(path, { token, filename, headers = {} } = {}) {
  let response = await fetch(apiUrl(path), { headers: authHeaders(token, null, headers), credentials: "include" });
  if (response.status === 401 && token) {
    const refreshedToken = await refreshAccessToken();
    if (refreshedToken) response = await fetch(apiUrl(path), { headers: authHeaders(refreshedToken, null, headers), credentials: "include" });
  }
  if (!response.ok) {
    const data = await parseResponse(response);
    throw apiError(response, data);
  }

  const blob = await response.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filenameFromDisposition(response.headers?.get?.("Content-Disposition")) || filename;
  link.click();
  URL.revokeObjectURL(link.href);
  return link.download;
}

export function createJobPoller(loadJob, { shouldContinue, intervalMs = 2000, onUpdate, immediate = false } = {}) {
  let timer = null;
  let stopped = false;

  const stop = () => {
    stopped = true;
    if (timer) window.clearTimeout(timer);
  };

  const tick = async () => {
    if (stopped) return;
    const job = await loadJob();
    onUpdate?.(job);
    if (!stopped && (shouldContinue?.(job) ?? false)) {
      timer = window.setTimeout(tick, intervalMs);
    }
  };

  timer = window.setTimeout(tick, immediate ? 0 : intervalMs);
  return stop;
}

export function rememberJob(key, job) {
  if (!key || !job?.id) return;
  window.localStorage?.setItem(key, JSON.stringify(job));
}

export function readRememberedJob(key) {
  if (!key) return null;
  try {
    const raw = window.localStorage?.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function forgetRememberedJob(key) {
  if (key) window.localStorage?.removeItem(key);
}

const authHeaders = (token, model, headers = {}) => {
  const result = { ...headers };
  if (token) result.Authorization = `Bearer ${token}`;
  return model ? withModelHeader(result, model) : result;
};

const apiUrl = (path) => `${API_BASE_URL}${path}`;

const parseResponse = async (response) => {
  if (response.status === 204) return null;
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const apiError = (response, data) => {
  const detail = data?.detail || data?.message;
  return new ApiError(detail || "The request failed.", { status: response.status, detail });
};

const filenameFromDisposition = (header = "") => header?.match(/filename="?([^"]+)"?/)?.[1];
